import { useState } from "react";
import { Link } from "react-router";
import { ArrowRight, Check, Minus } from "lucide-react";
import { useAuth } from "../../../lib/auth/useAuth";

type Cadence = "annual" | "monthly";

const PLANS = [
  {
    id: "free",
    name: "Free",
    tagline: "For teams under 10 people",
    priceMonthly: "$0",
    priceAnnual: "$0",
    cadenceLabel: "forever",
    cta: "Start free",
    ctaVariant: "outline" as const,
    highlights: [
      "Up to 9 users",
      "Unlimited receipts & invoices",
      "AI categorization & extraction",
      "Mileage tracking",
      "QuickBooks & Xero sync",
      "Audit-ready export (PDF + source docs)",
      "1-year retention",
      "Community support",
    ],
  },
  {
    id: "team",
    name: "Team",
    tagline: "When you need more structure",
    priceMonthly: "$10",
    priceAnnual: "$8",
    cadenceLabel: "per user / month",
    cta: "Start Team",
    ctaVariant: "primary" as const,
    featured: true,
    highlights: [
      "Everything in Free",
      "Unlimited users",
      "Policies & multi-step approvals",
      "Card-linked reconciliation",
      "Batch review & bulk actions",
      "Slack & Teams alerts",
      "7-year retention",
      "Priority email support",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Compliance-grade at scale",
    priceMonthly: "Custom",
    priceAnnual: "Custom",
    cadenceLabel: "",
    cta: "Contact sales",
    ctaVariant: "dark" as const,
    highlights: [
      "Everything in Team",
      "SSO / SAML & SCIM",
      "API & webhooks",
      "Multi-entity consolidation",
      "Custom retention & residency",
      "SOC 2 Type II report",
      "DPA & BAA available",
      "Dedicated CSM",
    ],
  },
];

type Row =
  | { kind: "category"; label: string }
  | { kind: "row"; label: string; values: [CellValue, CellValue, CellValue] };

type CellValue = true | false | string;

const MATRIX: Row[] = [
  { kind: "category", label: "Capture & sync" },
  { kind: "row", label: "Receipt, invoice, mileage capture", values: [true, true, true] },
  { kind: "row", label: "Email & chat forwarding", values: [true, true, true] },
  { kind: "row", label: "OCR & AI field extraction", values: [true, true, true] },
  { kind: "row", label: "Card-linked capture", values: [false, true, true] },
  { kind: "row", label: "Bank / card integrations", values: ["Unlimited", "Unlimited", "Unlimited"] },

  { kind: "category", label: "Records & categorization" },
  { kind: "row", label: "AI categorization", values: [true, true, true] },
  { kind: "row", label: "Custom categories", values: ["Up to 20", "Unlimited", "Unlimited"] },
  { kind: "row", label: "Vendor profiles & W-8/W-9", values: [true, true, true] },
  { kind: "row", label: "Narrative generation", values: [false, true, true] },

  { kind: "category", label: "Controls & workflow" },
  { kind: "row", label: "Policies (plain-English rules)", values: [false, true, true] },
  { kind: "row", label: "Multi-step approvals", values: [false, true, true] },
  { kind: "row", label: "Batch review & bulk actions", values: [false, true, true] },
  { kind: "row", label: "Anomaly flags", values: [false, true, true] },
  { kind: "row", label: "Slack / Teams alerts", values: [false, true, true] },

  { kind: "category", label: "Audit & export" },
  { kind: "row", label: "Live audit score", values: [true, true, true] },
  { kind: "row", label: "Auditor-ready PDF export", values: [true, true, true] },
  { kind: "row", label: "Public auditor view", values: [true, true, true] },
  { kind: "row", label: "Audit simulation", values: [false, true, true] },
  { kind: "row", label: "Document retention", values: ["1 year", "7 years", "Custom"] },

  { kind: "category", label: "Integrations" },
  { kind: "row", label: "QuickBooks / Xero", values: [true, true, true] },
  { kind: "row", label: "Stripe, Plaid, Gmail", values: [true, true, true] },
  { kind: "row", label: "API access", values: [false, "Read-only", "Full + webhooks"] },

  { kind: "category", label: "Admin & security" },
  { kind: "row", label: "Roles & permissions", values: ["Basic", "Standard", "Custom"] },
  { kind: "row", label: "SSO / SAML", values: [false, false, true] },
  { kind: "row", label: "SCIM provisioning", values: [false, false, true] },
  { kind: "row", label: "Audit log (admin actions)", values: [true, true, true] },
  { kind: "row", label: "Multi-entity consolidation", values: [false, false, true] },

  { kind: "category", label: "Support & compliance" },
  { kind: "row", label: "Support", values: ["Community", "Priority email", "Dedicated CSM"] },
  { kind: "row", label: "SLA", values: [false, false, "99.9% uptime"] },
  { kind: "row", label: "SOC 2 Type II report", values: [false, false, true] },
  { kind: "row", label: "DPA / BAA", values: [false, false, true] },
];

const FAQ = [
  {
    q: "Is the free plan really free forever?",
    a: "Yes. Up to 9 users with every feature an SMB needs to operate — unlimited receipts, AI categorization, mileage, integrations, and audit-ready export. No trial clock, no credit card. You pay only when you cross to a 10th seat or need compliance features like SSO or 7-year retention.",
  },
  {
    q: "What counts as a \"user\"?",
    a: "Anyone who logs in to Proofly — admins, employees, and external accountants sharing your workspace. Auditors who only view a read-only share link do NOT count as users.",
  },
  {
    q: "What happens when we hit the 10th seat?",
    a: "Your records stay intact. You'll be prompted to upgrade to Team to add the seat. No deletion, no lockout — we give you 14 days to decide.",
  },
  {
    q: "Can I pay monthly instead of annually?",
    a: "Yes. Team is $10/user/month when paid monthly, or $8/user/month billed annually. Swap cadence anytime from Settings.",
  },
  {
    q: "Do you have a nonprofit discount?",
    a: "Registered 501(c)(3) organizations get Team at 50% off. Email hello@proofly.one with your EIN and we'll set it up in one business day.",
  },
  {
    q: "Does Proofly replace my accountant?",
    a: "No — and it's not supposed to. Proofly keeps records complete and exportable so your accountant can do their best work. Many of our customers are referred to us by their accountants.",
  },
];

export function Pricing() {
  const [cadence, setCadence] = useState<Cadence>("annual");
  const { session } = useAuth();
  const ctaHref = session ? "/inbox" : "/login";

  return (
    <div className="w-full">
      {/* Hero */}
      <section
        className="relative"
        style={{
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-16 pb-10 md:pt-24 md:pb-14 text-center">
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-4">
            Pricing
          </div>
          <h1 className="font-display font-semibold text-[var(--ink-950)] tracking-[-0.03em] max-w-[820px] mx-auto" style={{ fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 1.08 }}>
            Priced for small businesses.
            <br />
            Free where it should be.
          </h1>
          <p className="mt-5 text-[18px] text-[var(--ink-700)] max-w-[620px] mx-auto">
            Three plans. No feature cliffs. No trial clock. Free forever for
            teams under 10.
          </p>

          <div className="mt-8 inline-flex items-center p-1 rounded-full border border-[var(--ink-200)] bg-white">
            {(
              [
                { id: "annual", label: "Annual", pill: "Save 20%" },
                { id: "monthly", label: "Monthly", pill: null },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                onClick={() => setCadence(opt.id)}
                className={[
                  "inline-flex items-center gap-2 px-5 h-9 rounded-full text-sm font-medium transition-colors",
                  cadence === opt.id
                    ? "bg-[var(--ink-950)] text-white"
                    : "text-[var(--ink-700)] hover:text-[var(--ink-900)]",
                ].join(" ")}
              >
                {opt.label}
                {opt.pill && (
                  <span
                    className={[
                      "text-[10px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-full",
                      cadence === opt.id
                        ? "bg-[var(--accent-amber)] text-[var(--ink-950)]"
                        : "bg-[var(--accent-amber-50)] text-[var(--warning)]",
                    ].join(" ")}
                  >
                    {opt.pill}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-8 pb-20 md:pb-24">
        <div className="grid gap-5 md:grid-cols-3">
          {PLANS.map((plan) => {
            const price =
              cadence === "annual" ? plan.priceAnnual : plan.priceMonthly;
            const cadenceCopy =
              plan.id === "free"
                ? "forever"
                : plan.id === "enterprise"
                  ? ""
                  : cadence === "annual"
                    ? "per user / month · billed annually"
                    : "per user / month · billed monthly";
            const featured = !!plan.featured;
            return (
              <div
                key={plan.id}
                className={[
                  "rounded-3xl p-8 flex flex-col",
                  featured
                    ? "bg-[var(--ink-950)] text-white border border-[var(--ink-950)] shadow-[var(--shadow-lg)]"
                    : "bg-white border border-[var(--ink-200)]",
                ].join(" ")}
              >
                <div className="flex items-center justify-between mb-4">
                  <div
                    className={[
                      "text-sm font-semibold",
                      featured ? "text-white" : "text-[var(--ink-950)]",
                    ].join(" ")}
                  >
                    {plan.name}
                  </div>
                  {featured && (
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] bg-[var(--accent-amber)] text-[var(--ink-950)] px-2 py-0.5 rounded-full">
                      Most popular
                    </span>
                  )}
                </div>
                <div
                  className={[
                    "text-[14px] mb-6",
                    featured ? "text-white/70" : "text-[var(--ink-700)]",
                  ].join(" ")}
                >
                  {plan.tagline}
                </div>
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-semibold text-[56px] leading-none tracking-[-0.03em]">
                      {price}
                    </span>
                  </div>
                  <div
                    className={[
                      "mt-2 text-[13px]",
                      featured ? "text-white/70" : "text-[var(--ink-500)]",
                    ].join(" ")}
                  >
                    {cadenceCopy}
                  </div>
                </div>
                <Link
                  to={plan.id === "enterprise" ? "mailto:hello@proofly.one" : ctaHref}
                  className={[
                    "inline-flex items-center justify-center gap-1.5 h-11 rounded-full font-semibold transition-colors",
                    plan.ctaVariant === "primary" &&
                      "bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white",
                    plan.ctaVariant === "dark" &&
                      "bg-white text-[var(--ink-950)] hover:bg-[var(--ink-100)]",
                    plan.ctaVariant === "outline" &&
                      "border border-[var(--ink-200)] bg-white hover:bg-[var(--ink-50)] text-[var(--ink-900)]",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <div
                  className={[
                    "mt-8 pt-6 border-t text-[11px] uppercase tracking-[0.1em] font-semibold",
                    featured
                      ? "border-white/10 text-white/60"
                      : "border-[var(--ink-200)] text-[var(--ink-500)]",
                  ].join(" ")}
                >
                  What's included
                </div>
                <ul className="mt-4 space-y-3 flex-1">
                  {plan.highlights.map((line) => (
                    <li key={line} className="flex items-start gap-2 text-[14px]">
                      <Check
                        className={[
                          "w-4 h-4 mt-0.5 shrink-0",
                          featured
                            ? "text-[var(--accent-amber)]"
                            : "text-[var(--brand-600)]",
                        ].join(" ")}
                        strokeWidth={2.5}
                      />
                      <span className={featured ? "text-white/90" : "text-[var(--ink-900)]"}>
                        {line}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-[var(--ink-50)] border-y border-[var(--ink-200)]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20">
          <div className="max-w-[680px] mb-10">
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-3">
              Compare plans
            </div>
            <h2 className="font-display font-semibold tracking-[-0.02em] text-[var(--ink-950)]" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
              Everything in each plan, side by side.
            </h2>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-[var(--ink-200)] bg-white">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-[var(--ink-200)]">
                  <th className="px-6 py-5 text-[12px] uppercase tracking-[0.08em] font-semibold text-[var(--ink-500)] w-[40%]">
                    Feature
                  </th>
                  {PLANS.map((p) => (
                    <th key={p.id} className="px-6 py-5">
                      <div className="font-semibold text-[var(--ink-950)]">{p.name}</div>
                      <div className="text-xs text-[var(--ink-500)] font-normal">
                        {p.id === "free"
                          ? "Free forever"
                          : p.id === "team"
                            ? cadence === "annual"
                              ? "$8/user/mo"
                              : "$10/user/mo"
                            : "Custom"}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MATRIX.map((row, i) =>
                  row.kind === "category" ? (
                    <tr key={`cat-${i}`} className="bg-[var(--ink-50)]">
                      <td
                        colSpan={4}
                        className="px-6 py-3 text-[11px] uppercase tracking-[0.1em] font-semibold text-[var(--ink-500)]"
                      >
                        {row.label}
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={`r-${i}`}
                      className="border-b border-[var(--ink-100)] last:border-0"
                    >
                      <td className="px-6 py-3.5 text-sm text-[var(--ink-900)]">
                        {row.label}
                      </td>
                      {row.values.map((v, j) => (
                        <td key={j} className="px-6 py-3.5 text-sm">
                          <Cell value={v} />
                        </td>
                      ))}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-[680px] mb-10">
          <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[var(--brand-700)] mb-3">
            FAQ
          </div>
          <h2 className="font-display font-semibold tracking-[-0.02em] text-[var(--ink-950)]" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
            Questions we get, answered directly.
          </h2>
        </div>
        <div className="divide-y divide-[var(--ink-200)] border-t border-b border-[var(--ink-200)]">
          {FAQ.map((item) => (
            <details key={item.q} className="group py-5">
              <summary className="flex items-center justify-between gap-6 cursor-pointer list-none">
                <span className="text-[16px] font-semibold text-[var(--ink-950)]">
                  {item.q}
                </span>
                <span
                  aria-hidden="true"
                  className="w-7 h-7 flex items-center justify-center rounded-full border border-[var(--ink-200)] bg-white group-open:bg-[var(--ink-950)] group-open:border-[var(--ink-950)] group-open:text-white transition-colors"
                >
                  <svg viewBox="0 0 12 12" className="w-3 h-3">
                    <line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <line x1="6" y1="2" x2="6" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="group-open:hidden" />
                  </svg>
                </span>
              </summary>
              <p className="mt-3 text-[15px] leading-relaxed text-[var(--ink-700)] max-w-[680px]">
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--ink-950)] text-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-20 text-center">
          <h2 className="font-display font-semibold tracking-[-0.02em] max-w-[620px] mx-auto" style={{ fontSize: "clamp(26px, 4vw, 40px)", lineHeight: 1.15 }}>
            Ready when you are.
          </h2>
          <p className="mt-4 text-[17px] text-white/70 max-w-[520px] mx-auto">
            Sign up in 30 seconds. Connect QuickBooks in one click. Start
            building complete records today.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={ctaHref}
              className="inline-flex items-center justify-center gap-1.5 h-12 px-7 rounded-full bg-white hover:bg-[var(--ink-100)] text-[var(--ink-950)] font-semibold transition-colors"
            >
              Start free <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="mailto:hello@proofly.one"
              className="inline-flex items-center justify-center h-12 px-7 rounded-full border border-white/20 hover:bg-white/10 text-white font-semibold transition-colors"
            >
              Talk to sales
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Cell({ value }: { value: CellValue }) {
  if (value === true)
    return (
      <Check className="w-5 h-5 text-[var(--success)]" strokeWidth={2.5} />
    );
  if (value === false)
    return <Minus className="w-5 h-5 text-[var(--ink-300)]" strokeWidth={2} />;
  return <span className="text-[var(--ink-900)] font-medium">{value}</span>;
}
